import { useState } from "react";
import { fmtCVE } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, Ruler } from "@phosphor-icons/react";

const UNITS = ["un", "m²", "m", "m³", "kg", "h", "dia", "vg"];

function AreaCalc({ onApply }) {
  const [open, setOpen] = useState(false);
  const [l, setL] = useState("");
  const [w, setW] = useState("");
  const area = (Number(l) || 0) * (Number(w) || 0);
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} className="flex items-center gap-1 text-[11px] font-medium text-primary"
      data-testid="area-calc-open"><Ruler size={13} /> Calcular m²</button>
  );
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="area-calc">
      <Input className="h-7 w-20" type="number" step="0.01" placeholder="Comp. m" value={l} onChange={(e) => setL(e.target.value)} data-testid="area-length" />
      ×
      <Input className="h-7 w-20" type="number" step="0.01" placeholder="Larg. m" value={w} onChange={(e) => setW(e.target.value)} data-testid="area-width" />
      = <b>{area.toFixed(2)} m²</b>
      <Button type="button" size="sm" className="h-7 px-2 text-xs" onClick={() => { onApply(Number(area.toFixed(2))); setOpen(false); }} data-testid="area-apply">Aplicar</Button>
    </div>
  );
}

export function computeTotals(items) {
  let subtotal = 0, discount = 0, vat = 0;
  items.forEach((i) => {
    const gross = Number(i.quantity || 0) * Number(i.unit_price || 0);
    const disc = (gross * Number(i.discount || 0)) / 100;
    const net = gross - disc;
    subtotal += gross;
    discount += disc;
    vat += (net * Number(i.vat_rate || 0)) / 100;
  });
  const net = subtotal - discount;
  return { subtotal, discount_total: discount, net, vat_total: vat, total: net + vat };
}

export function DocItemsEditor({ items, setItems, products }) {
  const add = () =>
    setItems([...items, { description: "", unit: "un", quantity: 1, unit_price: 0, discount: 0, vat_rate: 15 }]);

  const update = (idx, key, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: val };
    setItems(next);
  };

  const pickProduct = (idx, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    const next = [...items];
    next[idx] = {
      ...next[idx], product_id: p.id, description: p.name, unit: p.unit || "un",
      unit_price: p.price, vat_rate: p.vat_rate,
    };
    setItems(next);
  };

  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const t = computeTotals(items);

  return (
    <div className="space-y-3" data-testid="doc-items-editor">
      <div className="space-y-2">
        {items.map((it, idx) => {
          const lineNet = Number(it.quantity || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount || 0) / 100);
          return (
            <div key={idx} className="rounded-lg border border-border bg-secondary/40 p-3" data-testid={`line-item-${idx}`}>
              <div className="mb-2 flex gap-2">
                <Select onValueChange={(v) => pickProduct(idx, v)}>
                  <SelectTrigger className="h-8 w-44 shrink-0 text-xs" data-testid={`line-product-${idx}`}>
                    <SelectValue placeholder="Produto..." />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input className="h-8 flex-1" placeholder="Descrição" value={it.description}
                  onChange={(e) => update(idx, "description", e.target.value)} data-testid={`line-desc-${idx}`} />
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                  onClick={() => remove(idx)} data-testid={`line-remove-${idx}`}>
                  <Trash size={15} />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                <div>
                  <span className="text-[10px] text-muted-foreground">Unid.</span>
                  <Select value={it.unit || "un"} onValueChange={(v) => update(idx, "unit", v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`line-unit-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <span className="text-[10px] text-muted-foreground">Qtd</span>
                  <Input className="h-8" type="number" step="0.01" value={it.quantity}
                    onChange={(e) => update(idx, "quantity", e.target.value)} data-testid={`line-qty-${idx}`} />
                </div>
                <div>
                  <span className="text-[10px] text-muted-foreground">Preço</span>
                  <Input className="h-8" type="number" step="0.01" value={it.unit_price}
                    onChange={(e) => update(idx, "unit_price", e.target.value)} data-testid={`line-price-${idx}`} />
                </div>
                <div>
                  <span className="text-[10px] text-muted-foreground">Desc%</span>
                  <Input className="h-8" type="number" step="0.01" value={it.discount}
                    onChange={(e) => update(idx, "discount", e.target.value)} data-testid={`line-disc-${idx}`} />
                </div>
                <div>
                  <span className="text-[10px] text-muted-foreground">IVA%</span>
                  <Input className="h-8" type="number" step="0.01" value={it.vat_rate}
                    onChange={(e) => update(idx, "vat_rate", e.target.value)} data-testid={`line-vat-${idx}`} />
                </div>
                <div>
                  <span className="text-[10px] text-muted-foreground">Total</span>
                  <div className="flex h-8 items-center text-sm font-medium">{fmtCVE(lineNet)}</div>
                </div>
              </div>
              {it.unit === "m²" && (
                <div className="mt-2"><AreaCalc onApply={(a) => update(idx, "quantity", a)} /></div>
              )}
            </div>
          );
        })}
      </div>

      <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={add} data-testid="add-line-btn">
        <Plus size={15} className="mr-1" /> Adicionar linha
      </Button>

      <div className="ml-auto max-w-[240px] space-y-1 pt-2 text-sm">
        <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{fmtCVE(t.subtotal)}</span></div>
        <div className="flex justify-between text-muted-foreground"><span>Desconto</span><span>-{fmtCVE(t.discount_total)}</span></div>
        <div className="flex justify-between text-muted-foreground"><span>IVA</span><span>{fmtCVE(t.vat_total)}</span></div>
        <div className="flex justify-between border-t border-border pt-1 text-base font-bold text-primary">
          <span>Total</span><span data-testid="doc-total">{fmtCVE(t.total)}</span>
        </div>
      </div>
    </div>
  );
}
